import React from 'react';
import { Tooltip } from 'antd'
import Loader from "../../Packages/Loader";
import SVGIcons from '../SVGIcons';
import parse from "html-react-parser";

import { DISPLAY_COLOUR_LOGIC_CUSTOM_THRESHOLD, DISPLAY_COLOUR_LOGIC_DELTA, HYPHEN, NO_DATA_MESSAGE, RESPONSES } from './constants';


export const addColumnProperties = (column, headers, props) => {
    let updatedColumn = addCssToColumns(column, headers, props);
    return updatedColumn;
};

export const getCollapsedTitle = (title) => {
    return (
        <div title={title} class="heatmap-text-block heatmap-text-area heatmap-bold">
            <p className="text-block">{title}</p>
            <span className='heatmap-pointer-cursor'>
                <SVGIcons type={'SVG-plus'} />
            </span>
        </div>)
}



const addCssToColumns = (column, headers, props) => {
    let updatedColumn = column;
    if (updatedColumn.isStatic) {
        updatedColumn.render = (data, record) => {
            return {
                children: getColumnCss({
                    data,
                    isStatic: true,
                    isResponses: updatedColumn.isResponses,
                    column: updatedColumn,
                    row: record,
                    index: updatedColumn.dataIndex,
                    showScores: props.showScores,
                    hideColors: props.hideColors,
                    isCustomPalette: props.isCustomPalette,
                    heatmapConfiguration: props.heatmapConfiguration,
                    isTrendHeatmap: props.isTrendHeatmap,
                    selectedLevel: props.selectedLevel,
                    projectDetails: props.projectDetails,
                    benchmarkStyle: props.benchmarkStyle,
                    trendStyle: props.trendStyle

                }),
            };
        };
    } else {
        updatedColumn.render = (data, record) => {
            return {
                children: getColumnCss({
                    data,
                    isStatic: false,
                    isResponses: false,
                    row: record,
                    headers: headers,
                    index: updatedColumn.dataIndex,
                    showScores: props.showScores,
                    hideColors: props.hideColors,
                    isCustomPalette: props.isCustomPalette,
                    heatmapConfiguration: props.heatmapConfiguration,
                    isTrendHeatmap: props.isTrendHeatmap,
                    selectedLevel: props.selectedLevel,
                    projectDetails: props.projectDetails,
                    benchmarkStyle: props.benchmarkStyle,
                    trendStyle: props.trendStyle

                }),
            };
        };
        updatedColumn.title = updatedColumn.isCollapse ? getCollapsedTitle(updatedColumn.title) : (
            <div className={`${updatedColumn.isParent ? 'heatmap-parent-text' : updatedColumn.children && updatedColumn.children.length ?
                'heatmap-pointer-cursor heatmap-text-area' : 'heatmap-text-area'}
                    ${updatedColumn.isParent ? ' heatmap-bold' : ' '}`}>
                {getUpdatedColumnTitle(updatedColumn)}
            </div>
        );
    }
    return updatedColumn;
};

const getUpdatedColumnTitle = (updatedColumn) => {
    let typeofTitle = typeof (updatedColumn.title)
    if (typeofTitle !== "object") {
        if (updatedColumn && updatedColumn.isParent) {
            if (updatedColumn.title.length > 30) {
                return <Tooltip title={updatedColumn.title} placement='bottom'
                    overlayClassName='heatmap-toptooltip'>
                    {updatedColumn.title.substring(0, 30)}...
                </Tooltip>
            } else {
                return updatedColumn.title
            }
        }
        return <Tooltip title={updatedColumn.title} placement='bottom'
            overlayClassName='heatmap-toptooltip'
        >
            {updatedColumn.title}</Tooltip>
    }
    else {
        return updatedColumn.title
    }


}

// ---- Benchmark variant cell rendering (opt-in via the `benchmarkStyle` prop) ----
// Shades each numeric cell by its distance from the row's benchmark (All but Self),
// marks below-favourable scores with a red ▼, and renders the Agreement
// column. Only runs when benchmarkStyle is passed, so the shared
// tree-heatmap consumers are untouched.
export const bmShade = (delta) => {
    if (delta >= 0.5) return '#86EFAC'; // well above benchmark
    if (delta > 0.05) return '#C1F4D4'; // moderately above benchmark
    if (delta >= -0.05) return '#F8FAFC'; // close to benchmark
    if (delta > -0.5) return '#FBCFCF'; // moderately below benchmark
    return '#FCA5A5'; // well below benchmark
};

const bmAgreement = (level) => {
    if (level === 'low') {
        return (
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '3px 9px', borderRadius: 999, background: '#eef0f2', color: '#5b646f', fontSize: 11, fontWeight: 600, letterSpacing: '0.03em', textTransform: 'uppercase' }}>
                &#9888; Low
            </span>
        );
    }
    const filled = level === 'high' ? 3 : 2;
    return (
        <span style={{ display: 'inline-flex', gap: 4 }}>
            {[0, 1, 2].map((i) => (
                <span key={i} style={{ width: 7, height: 7, borderRadius: '50%', background: i < filled ? '#5b646f' : '#d5d9e0' }} />
            ))}
        </span>
    );
};

const getBenchmarkColumnCss = ({ data, isStatic, isResponses, row, index, benchmarkStyle }) => {
    const { benchmarkIndex, favorableMin = 4 } = benchmarkStyle;
    const isResponsesRow = row && row.name === 'Responses';

    // Category name column, with a ★ for critical skills (kept off the `name`
    // field so the Excel export stays clean).
    if (isStatic && !isResponses) {
        const nameEl = formatTopicsAndQuestions(data, row.children ? 90 : 100);
        if (row && row.critical) {
            return (
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    {nameEl}
                    <span style={{ color: '#f49753', fontSize: 13, lineHeight: 1 }} title="Critical skill">&#9733;</span>
                </div>
            );
        }
        return nameEl;
    }

    // Agreement column
    if (index === 'Agreement') {
        return (
            <div className="heatmap-value no-color" style={{ display: 'flex', justifyContent: 'center' }}>
                {isResponsesRow ? '—' : bmAgreement(data)}
            </div>
        );
    }

    // Response counts row
    if (isResponsesRow) {
        const label = data == null ? '' : `n=${Number(data).toLocaleString()}`;
        return (
            <div className="heatmap-value no-color" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                {label}
            </div>
        );
    }

    // No data
    if (data == null || data === HYPHEN || isNaN(parseFloat(data))) {
        return <div className="heatmap-value no-color" />;
    }

    const value = parseFloat(data);
    const benchmark = parseFloat(row[benchmarkIndex]);
    const isBenchCol = index === benchmarkIndex;
    const bg = isBenchCol || isNaN(benchmark) ? undefined : bmShade(value - benchmark);
    const below = value < favorableMin;
    return (
        <div className="heatmap-value" style={{ backgroundColor: bg, color: below ? '#c0392b' : undefined, fontWeight: below ? 600 : undefined, display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 3 }}>
            {value.toFixed(2)}
            {below ? <span style={{ fontSize: 10 }}>&#9660;</span> : null}
        </div>
    );
};

// ---- Trend variant cell rendering (opt-in via the `trendStyle` prop) ----
// Same row/column shape as the benchmark variant, but each cell holds a pair of
// rounds rather than one score: { delta, earlier, latest, n }. Cells are shaded
// on a diverging scale centred on zero — red for a decline, green for an
// improvement — and a change inside the meaningful-change band is left unshaded,
// because it is noise rather than movement. Only runs when trendStyle is passed.
export const trendShade = (delta, meaningfulDelta) => {
    // Rounded to the two decimals the cell displays, so a cell reading "+0.30"
    // is never shaded as if it were below the band on a float's hair.
    const size = Math.round(Math.abs(delta) * 100) / 100;
    if (size < meaningfulDelta) return undefined; // within the normal range
    // Three steps either side, so a 0.30 shift doesn't look like a 0.55 one.
    // End colours are the benchmark heatmap's, to keep the two grids one family.
    if (delta > 0) {
        if (size >= 0.5) return '#86EFAC';
        return size >= 0.4 ? '#A3EDBF' : '#C1F4D4';
    }
    if (size >= 0.5) return '#FCA5A5';
    return size >= 0.4 ? '#FBBABA' : '#FBCFCF';
};

const getTrendColumnCss = ({ data, isStatic, isResponses, row, index, trendStyle }) => {
    const { meaningfulDelta = 0.3 } = trendStyle;

    // Category name column, with a ★ for critical skills — same treatment as
    // the benchmark variant so the two heatmaps read alike.
    if (isStatic && !isResponses) {
        const nameEl = formatTopicsAndQuestions(data, row && row.children ? 90 : 100);
        if (row && row.critical) {
            return (
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    {nameEl}
                    <span style={{ color: '#f49753', fontSize: 13, lineHeight: 1 }} title="Critical skill">&#9733;</span>
                </div>
            );
        }
        return nameEl;
    }

    // No data — nothing to draw, same as the benchmark variant's empty cell.
    if (!data || data.delta == null) {
        return <div className="heatmap-value no-color" />;
    }

    const { delta, earlier, latest, n } = data;
    const bg = trendShade(delta, meaningfulDelta);
    const sign = delta < 0 ? '−' : '+';
    // Unshaded means the change is inside the normal range, so its number is
    // muted too — colour alone would not say so in the printed PDF.
    const isMuted = bg === undefined;

    // .heatmap-value already gives the cell its box, gutters and centring —
    // the same one the benchmark variant sits in. Only the stacking of the two
    // lines is added on top of it, plus textAlign: the flex centring places
    // each line as a box, but a line long enough to wrap still needs its own
    // text centred.
    return (
        <div className="heatmap-value no-color" style={{ backgroundColor: bg, flexDirection: 'column', textAlign: 'center', padding: '35px 10px', }}>
            <span style={{ color: isMuted ? '#8c949e' : '#1f2933', fontWeight: isMuted ? 400 : 600 }}>
                {`${sign}${Math.abs(delta).toFixed(2)}`}
            </span>
            <span style={{ color: '#8c949e', fontSize: 11 }}>
                {earlier != null && latest != null ? `${earlier.toFixed(2)} → ${latest.toFixed(2)}` : ''}
                {n != null ? ` · n=${Number(n).toLocaleString()}` : ''}
            </span>
        </div>
    );
};

const getColumnCss = ({ data, isStatic, isResponses, row, headers, index, column, showScores, hideColors, isCustomPalette, heatmapConfiguration, isTrendHeatmap, selectedLevel, projectDetails, benchmarkStyle, trendStyle }) => {
    if (trendStyle) {
        return getTrendColumnCss({ data, isStatic, isResponses, row, index, trendStyle });
    }
    if (benchmarkStyle) {
        return getBenchmarkColumnCss({ data, isStatic, isResponses, row, index, benchmarkStyle });
    }
    let trendData = { isTrendHeatmap, index, selectedLevel, projectDetails, currentSurveyValue: getCurrentProjectData({ index, isTrendHeatmap, projectDetails }, row, data), isColored: true }
    if (isStatic) {
        if (isResponses) {
            //for Overall
            return <div className="heatmap-value no-color">{data}</div>;
        } else {
            //for - Topics
            return (
                row.children ? formatTopicsAndQuestions(data, 60)
                    : formatTopicsAndQuestions(data, 68)

            );
        }
    } else {
        let backgroundColor = !hideColors && isCustomPalette ? handleColourFromPalette({ value: data, row, heatmapConfiguration, trendData }) : false
        let isDark = backgroundColor ? isColorDark(backgroundColor) : ""
        //return the below code to show tooltip when hovering

        // (<Tooltip arrowPointAtCenter={false} title={getCellTooltipElement({ headers, index, data, row, showScores })} overlayClassName="heatmap-tooltip" >
        //     <div className={`heatmap-value ${!hideColors && getCellColorClass(data, row)}`}>
        //         {formatCellNumbers(data, row, showScores)}
        //     </div>
        // </Tooltip>)
        //for all number cells
        return (
            <div className={`heatmap-value ${!hideColors && !isCustomPalette ? getCellColorClass(data, row, trendData) : ""}`} style={!hideColors && isCustomPalette ? { backgroundColor: backgroundColor, color: isDark ? 'white' : '#29363D' } : {}}>
                {formatCellNumbers(data, row, showScores, trendData)}
            </div>
        );
    }
};

const formatTopicsAndQuestions = (data, maxLength) => {
    return (data.length > maxLength ?
        <Tooltip title={data}>
            <div className="heatmap-name">
                <div className="heatmap-name__block">
                    {parse(data.substring(0, maxLength) + '...')}
                </div>
            </div>
        </Tooltip>
        : <div className="heatmap-name">
            {parse(data)}
        </div>)
}

const formatCellNumbers = (value, row, showScores, trendHeatmapData) => {
    if (row && row.name === "Responses") {
        return value || 0
    }
    if (showScores) {
        // return `+${value}`
        return value
        // return calculateResponses(value, row)
    }
    else {
        if (trendHeatmapData.isTrendHeatmap) {
            if (value === HYPHEN) {
                return value
            }
            if (trendHeatmapData.selectedLevel && trendHeatmapData.selectedLevel.some((projectId => trendHeatmapData.index.includes(projectId)))) {
                let splittedValue = trendHeatmapData.index.split("/**")
                let currentProjectValue = row[`${splittedValue[0]}/**${trendHeatmapData.projectDetails.projectName}_${trendHeatmapData.projectDetails.projectId}`]
                if (splittedValue && splittedValue[0] && currentProjectValue !== undefined) {
                    if (currentProjectValue === HYPHEN) {
                        return value
                    } else {
                        let calculatedTrendDifference = currentProjectValue - value
                        if (calculatedTrendDifference > 0) {
                            return `+${calculatedTrendDifference}`
                        }
                        else {
                            return calculatedTrendDifference
                        }


                    }
                }

            }
            return value
        }
        else {
            let gapValue = calculateResponses(value, row)
            if (gapValue > 0) {
                return `+${gapValue}`

            }
            else {
                return `${gapValue || 0}`

            }
        }
    }



}

const calculateResponses = (value, row) => {
    if (value === HYPHEN) {
        return value
    }
    let valueData = isNaN(parseInt(value)) ? 0 : value
    return valueData - row.score
}

const getCellColorClass = (value, row, trendData) => {
    let valueData = isNaN(parseInt(value)) ? 0 : (value - row.score)
    let className = "";
    if (row && row.name === "Responses") {
        return "no-color"
    }

    else if (trendData && trendData.isTrendHeatmap && trendData.selectedLevel && !trendData.selectedLevel.some((projectId => trendData.index.includes(projectId)))) {
        return "no-color"
    }

    if (parseInt(valueData) > 15) 
        className = "blue";
    else if (parseInt(valueData) > 10 && parseInt(valueData) <= 15)
        className = "light-blue";
    else if (parseInt(valueData) > 5 && parseInt(valueData) <= 10)
        className = "light-light-blue";
    else if (parseInt(valueData) > 3 && parseInt(valueData) <= 5)
        className = "light-light-light-blue";
    else if (parseInt(valueData) >= -3 && parseInt(valueData) <= 3)
        className = "no-color";
    else if (parseInt(valueData) < -3 && parseInt(valueData) >= -5)
        className = "light-light-orange";
    else if (parseInt(valueData) < -5 && parseInt(valueData) >= -10)
        className = "light-orange";
    else if (parseInt(valueData) < -10 && parseInt(valueData) >= -15)
        className = "orange";
    else if (parseInt(valueData) <-15) 
        className = "red";
    else className = "";

    return className;
}




export const customExpandIcon = (props) => {
    if (!props.expandable) {
        return null
    }
    if (props.expanded) {
        if (!props.expandable.length) {
            return getLoader();
        }
        return getAntdDefaultExpandButton(true, props)
    } else {
        return getAntdDefaultExpandButton(false, props)
    }
}

const getLoader = () => {
    return <button
        type="button"
        className="heatmap-expand-loader"
    ><Loader
            test-name="loader-spin"
            classList={["xs dark"]}
        /></button>

}

const getAntdDefaultExpandButton = (isExpanded, props) => {
    return <button
        data-html2canvas-ignore="true"
        type="button"
        onClick={e => {
            props.onExpand(props.record, e);
        }}
        class={
            `ant-table-row-expand-icon
         ant-table-row-expand-icon-${isExpanded ? 'expanded' : 'collapsed'}`
        }
        aria-label="Collapse row"></button>
}

const getCellTooltipElement = ({ headers, index, data, row, showScores }) => {
    let valueData = isNaN(parseInt(data)) ? 0 : data
    if (data === HYPHEN) {
        return NO_DATA_MESSAGE
    }
    const values = index.split('_')
    let renderTooltip = headers && headers.length > 0 && row.name === RESPONSES ? <ul>{headers.map((item, index) =>
        values && values[index] && <li>
            <span>{item}</span>:&nbsp;<b>{values[index]}</b>
        </li>)}</ul> : row.name !== RESPONSES ?
        <ul>
            {headers.map((item, index) =>
        values && values[index] && <li>
            <span>{item}</span>:&nbsp;<b>{values[index]}</b>
        </li>)}
                <li><span>Score</span>:&nbsp;<b>{valueData + row.score}</b></li>

                {(!showScores && row.name !== RESPONSES) && <li><span>Gap score</span>:&nbsp;<b>{formatTooltipScores(valueData)}</b></li>}
        </ul> : ""
    return renderTooltip
    //  <ul>{headers.map((item, index) =>
    //     values && values[index] && <li>
    //         <span>{item}</span>:&nbsp;<b>{values[index]}</b>
    //     </li>)}
    //     {row.name !== RESPONSES && <li><span>Score</span>:&nbsp;<b>{valueData + row.score}</b></li>}
    //     {(!showScores && row.name !== RESPONSES) && <li><span>Gap score</span>:&nbsp;<b>{formatTooltipScores(valueData)}</b></li>}
    // </ul>

}

const formatTooltipScores = (score) => {
    if (score > 0) return `+${score}`
    return score || 0
}

// Excel-export coloring for the benchmark heatmap variant — mirrors
// getBenchmarkColumnCss on screen: every numeric cell is shaded by its
// distance from the row's benchmark column (bmShade), while the Agreement
// column and the benchmark column itself are left uncoloured.
// Trend heatmaps shade the exported sheet by the change each cell reports, the
// same diverging scale used on screen — not against `score` like the generic
// gap/showScores flow, which reads a trend cell as an unparseable score and
// fills it black.
export const getTrendColouredExcelSheet = (sheet, columns, trendStyle) => {
    const { meaningfulDelta = 0.3 } = trendStyle
    sheet.rows.forEach((rowElement) => {
        if (rowElement.cells[0].value === "" || rowElement.cells[0].value === "Responses") {
            return
        }
        rowElement.cells.forEach((cellElement, cellIndex) => {
            const column = columns[cellIndex]
            if (!column || (column.isStatic && column.dataIndex === 'name')) {
                return
            }
            // Cells were flattened to "+0.22 (5.03 to 5.25, n=2342)" on the way
            // in (see getScoredElement); parseFloat takes the leading signed
            // change and ignores the rest. A cell with no data gives NaN.
            const delta = parseFloat(cellElement.value)
            if (isNaN(delta)) {
                return
            }
            const shade = trendShade(delta, meaningfulDelta)
            if (!shade) {
                return // inside the normal range — left unfilled, as on screen
            }
            cellElement.style.fill.fgColor = shade.replace('#', '').toLowerCase()
            cellElement.style.fill.patternType = "solid"
        })
    })
    return sheet
}

export const getBenchmarkColouredExcelSheet = (sheet, columns, benchmarkStyle) => {
    const { benchmarkIndex } = benchmarkStyle
    const benchmarkColIndex = columns.findIndex((col) => col.dataIndex === benchmarkIndex)
    sheet.rows.forEach((rowElement) => {
        if (rowElement.cells[0].value === "" || rowElement.cells[0].value === "Responses") {
            return
        }
        const benchmarkValue = benchmarkColIndex > -1 ? parseFloat(rowElement.cells[benchmarkColIndex].value) : NaN
        rowElement.cells.forEach((cellElement, cellIndex) => {
            const column = columns[cellIndex]
            if (!column || (column.isStatic && column.dataIndex === 'name') || column.dataIndex === 'Agreement' || column.dataIndex === benchmarkIndex) {
                return
            }
            const value = parseFloat(cellElement.value)
            if (isNaN(value) || isNaN(benchmarkValue)) {
                return
            }
            cellElement.style.fill.fgColor = bmShade(value - benchmarkValue).replace('#', '').toLowerCase()
            cellElement.style.fill.patternType = "solid"
        })
    })
    return sheet
}

export const getColouredExcelSheet = (sheet, configuration, isTrendHeatmap, isGapScore, noncoloringIndex = 1) => {
    let sheetCopy = sheet
    sheetCopy.rows.forEach((rowElement, rowIndex) => {
        if (!(rowElement.cells[0].value === "" || rowElement.cells[0].value === "Responses")) {
            rowElement.cells.forEach((cellElement, cellIndex) => {
                if (cellIndex > noncoloringIndex) {
                    let originalScore
                    let cellValueForComputingColour
                    let isColored = false
                    if (typeof (rowElement.cells[cellIndex].value) == "string" && rowElement.cells[cellIndex].value.includes("currentScore") && isTrendHeatmap) {
                        isColored = true // given this for identify this is trend matched survey and it needs colour
                        let currentScoreSplits = rowElement.cells[cellIndex].value.split("currentScore")
                        let splittedArrayScore = currentScoreSplits[0].split("|")
                        cellValueForComputingColour = !isGapScore ? parseFloat(splittedArrayScore[0]) : parseFloat(currentScoreSplits[currentScoreSplits.length - 1]) - parseFloat(splittedArrayScore[0])
                        sheetCopy.rows[rowIndex].cells[cellIndex].value = parseFloat(splittedArrayScore[0])
                        originalScore = parseFloat(splittedArrayScore[1])
                    }
                    else if (typeof (rowElement.cells[cellIndex].value) == "string" && rowElement.cells[cellIndex].value.includes("|")) {
                        let splittedArray = rowElement.cells[cellIndex].value.split("|")
                        sheetCopy.rows[rowIndex].cells[cellIndex].value = parseFloat(splittedArray[0])
                        originalScore = parseFloat(splittedArray[1])
                        cellValueForComputingColour = splittedArray[2] ? parseFloat(splittedArray[2]) : parseFloat(splittedArray[0])

                    }

                    else {
                        originalScore = rowElement.cells[cellIndex].value
                        cellValueForComputingColour = rowElement.cells[cellIndex].value
                    }
                    let cellClass = configuration ? handleColourFromPalette({ value: cellValueForComputingColour, heatmapConfiguration: configuration, isFromExport: true, row: { score: originalScore }, trendData: { isTrendHeatmap, currentSurveyValue: cellValueForComputingColour, isColored } }) : getCellColorClass(cellValueForComputingColour, { score: originalScore })
                    let cellColour = colourPicker[cellClass] ? colourPicker[cellClass] : cellClass
                    // sheetCopy.rows[rowIndex].cells[cellIndex].style.align.v = "center"
                    // sheetCopy.rows[rowIndex].cells[cellIndex].style.align.h = "center"
                    sheetCopy.rows[rowIndex].cells[cellIndex].style.fill.fgColor = cellColour
                    sheetCopy.rows[rowIndex].cells[cellIndex].style.fill.patternType = "solid"
                    // rowElement.cells[cellIndex].style.border.bottom = "thin"
                    // rowElement.cells[cellIndex].style.border.left = "thin"
                    // rowElement.cells[cellIndex].style.border.right = "thin"
                }
                else {
                    if (cellIndex === 0 || cellIndex < noncoloringIndex) {
                        sheetCopy.rows[rowIndex].cells[cellIndex].style.align.v = "bottom"
                        sheetCopy.rows[rowIndex].cells[cellIndex].style.align.h = "general"
                    }

                }

            })
        }
        else {
            sheetCopy.rows[rowIndex].cells[0].style.align.v = "bottom"
            sheetCopy.rows[rowIndex].cells[0].style.align.h = "general"
        }
    })
    return sheetCopy


}

const colourPicker = {
    "light-blue": "74add1",
    "light-light-blue": "abd9e9",
    "light-light-light-blue": "c9eff3",
    "no-color": "f9f9f6",
    "light-light-orange": "fee090",
    "light-orange": "fdae61",
    "orange": "f46d43",
    "red": "d73027",
    "blue": "4575b4"

}

const getCurrentProjectData = (trendData, row, data) => {
    if (trendData && trendData.isTrendHeatmap) {
        let splittedValue = trendData.index.split("/**")
        let calculatedValue = row[`${splittedValue[0]}/**${trendData.projectDetails.projectName}_${trendData.projectDetails.projectId}`]
        return isNaN(parseInt(data)) ? 0 : calculatedValue - data
    }
    else {
        return null
    }
}

const handleColourFromPalette = ({ value, row, heatmapConfiguration, isFromExport, trendData }) => {
    if (row && row.name === "Responses") {
        return isFromExport ? "#f9f9f6".slice(1) : "#f9f9f6"
    }
    else if (trendData && trendData.isTrendHeatmap && trendData.selectedLevel && !trendData.selectedLevel.some((projectId => trendData.index.includes(projectId)))) {
        return "#f9f9f6"
    }
    let valueData = isNaN(parseInt(value)) ? 0 : (value - row.score)
    if (trendData && trendData.isTrendHeatmap && trendData.isColored) {
        valueData = trendData.currentSurveyValue
    }
    if (heatmapConfiguration.displayColorLogic === DISPLAY_COLOUR_LOGIC_DELTA) {
        let minimumValueData = heatmapConfiguration.thresholdValue[0]
        let maximumValueData = heatmapConfiguration.thresholdValue[2]
        if (Math.abs(parseInt(valueData)) < minimumValueData.value) {
            return isFromExport ? "#f9f9f6".slice(1) : "#f9f9f6"
        }
        else if ((parseInt(valueData)) >= maximumValueData.value) {
            return isFromExport ? maximumValueData.maxColor.slice(1) : maximumValueData.maxColor
        }
        let findedObject = heatmapConfiguration.thresholdValue.find((item, index) => Math.abs(parseInt(valueData)) <= item.maxValue && Math.abs(parseInt(valueData)) >= item.value)
        if (parseInt(valueData) < 0) {
            let color = getColorForExport({ findedObject, defaultColor: "#d73027", isFromExport, colorKey: "minColor" })
            return color
        }
        else {
            let color = getColorForExport({ findedObject, defaultColor: "#4575b4", isFromExport, colorKey: "maxColor" })
            return color
        }

    }
    else if (heatmapConfiguration.displayColorLogic === DISPLAY_COLOUR_LOGIC_CUSTOM_THRESHOLD) {
        let originalValue = calculateResponses(value, row)
        let findedObject = heatmapConfiguration.thresholdValue.find((item, index) => Math.abs(parseInt(originalValue)) <= item.value)
        let maxValue = heatmapConfiguration.thresholdValue[heatmapConfiguration.thresholdValue.length - 1]
        if (originalValue > maxValue.value) {
            let color = getColorForExport({ findedObject: maxValue, defaultColor: maxValue.color, isFromExport, colorKey: "color" })

            return color
        }
        else {
            let color = getColorForExport({ findedObject, defaultColor: "#f9f9f6", isFromExport, colorKey: "color" })
            return color
        }
    }
}

const getColorForExport = ({ findedObject, defaultColor, isFromExport, colorKey }) => {
    const color = findedObject ? findedObject[colorKey] : defaultColor;
    return isFromExport ? color.slice(1) : color;
};

function isColorDark(hexColor) {
    // Convert hex to RGB
    hexColor = hexColor.replace(/^#/, '');
    const bigint = parseInt(hexColor, 16);
    const r = (bigint >> 16) & 255;
    const g = (bigint >> 8) & 255;
    const b = bigint & 255;

    // Convert RGB to HSL
    const rNormalized = r / 255;
    const gNormalized = g / 255;
    const bNormalized = b / 255;

    const max = Math.max(rNormalized, gNormalized, bNormalized);
    const min = Math.min(rNormalized, gNormalized, bNormalized);

    // Calculate lightness
    const lightness = (max + min) / 2;

    // If lightness is less than or equal to 0.5, the color is considered dark
    return lightness <= 0.65;
}